import { formatCurrency, InvoiceCommissionId, InvoiceId, UserId } from "@sp-crm/core";
import { UserSelect } from "components/shared/user-select";
import { DeleteButton } from "components/ui/action-button";
import { InlineBanner } from "components/ui/inline-banner";
import { Input } from "components/ui/input";
import { SecondaryButton } from "components/ui/secondary-button";
import { TooltipLabel } from "components/ui/tooltip-label";
import React from "react";
import { percentStringToBasisPoints } from "./rate-percent";

export interface ApiCommission {
    id: InvoiceCommissionId;
    userId: UserId;
    rateBasisPoints: number;
    locked: boolean;
}

export interface CommissionDraft {
    id: InvoiceCommissionId | null;
    userId: UserId | "";
    ratePercent: string;
    locked: boolean;
}

export interface CommissionInput {
    userId: UserId;
    rateBasisPoints: number;
}

export interface CommissionDraftDiff {
    toCreate: { draft: CommissionDraft; commission: CommissionInput }[];
    toUpdate: { id: InvoiceCommissionId; commission: CommissionInput }[];
    toRemove: InvoiceCommissionId[];
}

export const emptyCommissionDraft = (): CommissionDraft => ({
    id: null,
    userId: "",
    ratePercent: "",
    locked: false,
});

export const apiCommissionToDraft = (commission: ApiCommission): CommissionDraft => ({
    id: commission.id,
    userId: commission.userId,
    ratePercent: (commission.rateBasisPoints / 100).toString(),
    locked: commission.locked,
});

export const reconcileCommissionLocked = (
    draft: CommissionDraft,
    latest: ApiCommission,
): CommissionDraft => ({ ...draft, locked: latest.locked });

const draftToInput = (draft: CommissionDraft): CommissionInput => ({
    userId: draft.userId as UserId,
    rateBasisPoints: percentStringToBasisPoints(draft.ratePercent),
});

export const draftsToCommissionInputs = (drafts: CommissionDraft[]): CommissionInput[] =>
    drafts.filter(d => d.userId !== "" && d.ratePercent !== "").map(d => draftToInput(d));

export const diffCommissionDrafts = (
    original: ApiCommission[],
    drafts: CommissionDraft[],
): CommissionDraftDiff => {
    const validDrafts = drafts.filter(d => d.userId !== "" && d.ratePercent !== "");
    const draftIds = new Set(validDrafts.map(d => d.id).filter(id => id !== null));

    const toCreate = validDrafts
        .filter(d => d.id === null)
        .map(d => ({ draft: d, commission: draftToInput(d) }));

    const toUpdate = validDrafts
        .filter((d): d is CommissionDraft & { id: InvoiceCommissionId } => {
            if (d.id === null) {
                return false;
            }
            const originalCommission = original.find(o => o.id === d.id);
            if (!originalCommission) {
                return false;
            }
            const input = draftToInput(d);
            return (
                originalCommission.userId !== input.userId ||
                originalCommission.rateBasisPoints !== input.rateBasisPoints
            );
        })
        .map(d => ({ id: d.id, commission: draftToInput(d) }));

    const toRemove = original.filter(o => !draftIds.has(o.id)).map(o => o.id);

    return { toCreate, toUpdate, toRemove };
};

export interface CommissionSyncMutations {
    create: (input: {
        invoiceId: InvoiceId;
        commission: CommissionInput;
    }) => Promise<{ id: InvoiceCommissionId }>;
    update: (input: {
        id: InvoiceCommissionId;
        commission: CommissionInput;
    }) => Promise<unknown>;
    remove: (input: { id: InvoiceCommissionId }) => Promise<unknown>;
}

export const syncCommissionDrafts = async (
    invoiceId: InvoiceId,
    original: ApiCommission[],
    drafts: CommissionDraft[],
    mutations: CommissionSyncMutations,
    onDraftsChange: (drafts: CommissionDraft[]) => void,
): Promise<void> => {
    const diff = diffCommissionDrafts(original, drafts);
    let currentDrafts = drafts;

    for (const { draft, commission } of diff.toCreate) {
        const created = await mutations.create({ invoiceId, commission });
        currentDrafts = currentDrafts.map(d =>
            d === draft ? { ...d, id: created.id } : d,
        );
        onDraftsChange(currentDrafts);
    }
    for (const { id, commission } of diff.toUpdate) {
        await mutations.update({ id, commission });
    }
    for (const id of diff.toRemove) {
        await mutations.remove({ id });
    }
};

const formatCents = (cents: number): string => formatCurrency(cents / 100);

const ratePercentPattern = /^\d*\.?\d{0,2}$/;

export const totalCommissionBasisPoints = (drafts: CommissionDraft[]): number =>
    drafts.reduce((acc, d) => acc + percentStringToBasisPoints(d.ratePercent), 0);

export const totalCommissionPercent = (drafts: CommissionDraft[]): number =>
    totalCommissionBasisPoints(drafts) / 100;

export const validateCommissionDrafts = (drafts: CommissionDraft[]): string[] => {
    const errors: string[] = [];

    const userIds = drafts.map(d => d.userId).filter(userId => userId !== "");
    const duplicated = userIds.filter(
        (userId, index) => userIds.indexOf(userId) !== index,
    );
    if (duplicated.length > 0) {
        errors.push(
            "Each team member can only have one commission on this invoice. Remove the duplicate before saving.",
        );
    }

    const hasZeroRate = drafts.some(
        d =>
            d.userId !== "" &&
            d.ratePercent !== "" &&
            percentStringToBasisPoints(d.ratePercent) === 0,
    );
    if (hasZeroRate) {
        errors.push("Commission rate must be greater than 0%.");
    }

    return errors;
};

export interface CommissionTotals {
    owedCents: number;
    paidCents: number;
}

interface Props {
    visible: boolean;
    disabled: boolean;
    drafts: CommissionDraft[];
    totalCents: number;
    totals: CommissionTotals | undefined;
    onChange: (drafts: CommissionDraft[]) => void;
}

export const InvoiceCommissionsEditor: React.FC<Props> = ({
    visible,
    disabled,
    drafts,
    totalCents,
    totals,
    onChange,
}) => {
    if (!visible) {
        return null;
    }

    const updateDraft = (index: number, patch: Partial<CommissionDraft>) =>
        onChange(
            drafts.map((draft, i) => (i === index ? { ...draft, ...patch } : draft)),
        );

    const addDraft = () => onChange([...drafts, emptyCommissionDraft()]);

    const removeDraft = (index: number) => onChange(drafts.filter((_, i) => i !== index));

    const updateRatePercent = (index: number, value: string) => {
        if (!ratePercentPattern.test(value)) {
            return;
        }
        updateDraft(index, { ratePercent: value });
    };

    const totalPercent = totalCommissionPercent(drafts);
    const totalCommissionCents = Math.round((totalCents * totalPercent) / 100);
    const showCommissionTotal = totalCents > 0 && drafts.length > 0;
    const hasPaymentTotals = !!totals && (totals.owedCents > 0 || totals.paidCents > 0);
    const validationErrors = validateCommissionDrafts(drafts);

    return (
        <div className="space-y-4">
            <div>
                <div className="flex items-center gap-1.5">
                    <h3 className="font-semibold">Commissions</h3>
                    <TooltipLabel
                        align="center"
                        tooltip="A commission locks once it's been marked paid on the Commission Report, so the payout history stays accurate. To edit or remove a locked commission, first undo the paid status on the Commission Report."
                    />
                </div>
                <p className="text-sm text-gray-500">
                    Internal only — never shown on the community-facing invoice.
                </p>
            </div>

            {drafts.length === 0 ? null : (
                <div className="space-y-3">
                    {drafts.map((draft, index) => (
                        <div
                            key={index}
                            className="flex items-end gap-3 rounded-lg bg-gray-50 p-3">
                            <div className="flex flex-1 flex-wrap items-end gap-3">
                                <div className="flex-1 min-w-48">
                                    <UserSelect
                                        label="Team member"
                                        includeEveryone={false}
                                        includeUnassigned
                                        unassignedLabel="Select a team member"
                                        value={draft.userId}
                                        disabled={draft.locked || disabled}
                                        onChange={userId =>
                                            updateDraft(index, {
                                                userId: userId as UserId,
                                            })
                                        }
                                    />
                                </div>
                                <div className="w-24">
                                    <Input
                                        label="Rate %"
                                        type="number"
                                        min={0.01}
                                        max={100}
                                        step={0.01}
                                        value={draft.ratePercent}
                                        disabled={draft.locked || disabled}
                                        onChange={e =>
                                            updateRatePercent(index, e.target.value)
                                        }
                                    />
                                </div>
                            </div>
                            <div>
                                <div className="invisible mb-1">&nbsp;</div>
                                <DeleteButton
                                    backgroundColor="bg-white"
                                    size="sm"
                                    disabled={draft.locked || disabled}
                                    onClick={() => removeDraft(index)}
                                />
                            </div>
                        </div>
                    ))}
                </div>
            )}

            <SecondaryButton type="button" disabled={disabled} onClick={addDraft}>
                <span className="text-sm">Add commission</span>
            </SecondaryButton>

            {validationErrors.length > 0 ? (
                <InlineBanner type="error">
                    <ul className="list-disc pl-4 text-sm">
                        {validationErrors.map((error, index) => (
                            <li key={index}>{error}</li>
                        ))}
                    </ul>
                </InlineBanner>
            ) : null}

            {showCommissionTotal || hasPaymentTotals ? (
                <div className="text-sm text-gray-500">
                    {showCommissionTotal ? (
                        <>
                            Total commissions: {formatCents(totalCommissionCents)} (
                            {totalPercent.toFixed(2)}% of invoice)
                        </>
                    ) : null}
                    {showCommissionTotal && hasPaymentTotals ? " · " : null}
                    {hasPaymentTotals && totals ? (
                        <>
                            Owed: {formatCents(totals.owedCents)} · Paid:{" "}
                            {formatCents(totals.paidCents)}
                        </>
                    ) : null}
                </div>
            ) : null}
        </div>
    );
};
